import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { trackProConversion } from "../lib/gtag";
import { useAuth } from "../context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const res = await api.paymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setState("paid");
          trackProConversion(sessionId, res.amount ? res.amount / 100 : 8.99);
          refreshUser();
          return;
        }
        if (["failed", "expired"].includes(res.payment_status)) { setState("error"); return; }
      } catch {
        setState("error"); return;
      }
      if (attempts.current >= 8) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <main className="flex min-h-[80vh] items-center justify-center bg-slate-50 px-5 py-12">
      <Seo title="Payment status | FindMyFlight" description="Your FindMyFlight PRO payment status." path="/payment/success" />
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-blue-600" />
            <h1 className="mt-4 text-2xl font-bold text-slate-900">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-slate-500">Hang tight, this only takes a few seconds.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-green-500" />
            <h1 className="mt-4 text-2xl font-bold text-slate-900">You’re now PRO! 🎉</h1>
            <p className="mt-2 text-sm text-slate-600">Enjoy a full year of availability, live search, SMS alerts and more.</p>
            <div className="mt-6 flex justify-center gap-2">
              <Link to="/search"><Button className="gap-2 bg-blue-600 hover:bg-blue-700">Start searching <ArrowRight className="h-4 w-4" /></Button></Link>
              <Link to="/"><Button variant="outline">Home</Button></Link>
            </div>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 text-amber-500" />
            <h1 className="mt-4 text-2xl font-bold text-slate-900">Payment processing</h1>
            <p className="mt-2 text-sm text-slate-600">Your payment is still processing. It may take a moment to reflect — refresh this page shortly.</p>
            <Link to="/" className="mt-6 inline-block"><Button variant="outline">Back home</Button></Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-red-400" />
            <h1 className="mt-4 text-2xl font-bold text-slate-900">Something went wrong</h1>
            <p className="mt-2 text-sm text-slate-600">We couldn’t confirm your payment. If you were charged, it will still be applied.</p>
            <Link to="/pricing" className="mt-6 inline-block"><Button className="bg-blue-600 hover:bg-blue-700">Back to pricing</Button></Link>
          </>
        )}
      </div>
    </main>
  );
}
